/**
 * board-view — ЕДИНЫЙ модуль «Доска»: одна реализация структуры доски, на которую
 * ССЫЛАЮТСЯ все экраны (игра / result / реплей / превью доски дня). Никто не рисует
 * доску заново. owner-directive 2026-05-30: «доска — единый объект, на который
 * ссылаются экраны»; Вариант А — плашка одинакова ВЕЗДЕ: «Очки + Ходы + 3 шара».
 *
 * Модуль владеет:
 *   · разметкой: рамка `.board-frame` → плашка `.frame-next` (Очки | Ходы + 3 шара)
 *     + сетка 9×9 `.board` (81 клетка);
 *   · размерами: всё от `--cell-size` (board.css, единый токен) — эталон;
 *   · рендером: плашка (счёт/ходы/ниша) + статичная отрисовка поля.
 *
 * Визуал — board.css (.board-frame / .frame-next / .fn-* / .board / .cell / .ball).
 * Размещение/центрирование области доски — .postament-board (postament.css).
 *
 * Режимы:
 *   · static (по умолчанию) — снимок (шары `.ball.static`), без интерактива; экран
 *     зовёт renderStaticGrid + setScore/setMoves/renderNiche.
 *   · interactive (игра) — клетки получают role/aria/dataset; КЛИКИ и диф-рендер
 *     (выделение, анимации) вешает САМ экран на `handle.cells` (игровая логика
 *     остаётся в board-screen, но работает поверх ОБЩЕЙ структуры).
 */

import { N, NEXT_BALLS_PER_TURN } from '@lines98/shared/config/balance';
import type { CellValue, NextBall } from '@lines98/shared/types/game';

import { t } from './i18n/index.js';

/**
 * Подписи плашки/доски — ЛОКАЛИЗУЕМЫ (owner-баг 2026-06-23: «Очки/Ходы/Клетка не должны
 * быть русским шлейфом»). Источник — namespace `figures` в content/i18n (тот же блок, что
 * читают SEO-фигуры). По умолчанию берутся из `t()` по ГЛОБАЛЬНОМУ locale (живая игра);
 * экран может передать явный набор (напр. SEO-контекст — язык страницы) через opts.labels.
 */
export interface BoardViewLabels {
  /** Подпись «Очки» / Score. */
  readonly score: string;
  /** Подпись «Ходы» / Moves. */
  readonly moves: string;
  /** aria-label сетки (поле 9×9). */
  readonly fieldAria: string;
  /** Шаблон aria клетки с плейсхолдерами {r},{c} (1-based). */
  readonly cellAria: string;
}

/** Подписи по ГЛОБАЛЬНОМУ locale через `t()` (живая игра). Фолбэк-цепочка t() = lang→en→ключ. */
function defaultLabels(): BoardViewLabels {
  return {
    score: t('figures.score'),
    moves: t('figures.moves'),
    fieldAria: t('figures.fieldAria'),
    cellAria: t('figures.cellAria'),
  };
}

export interface BoardViewOptions {
  /** Игровой режим: клетки получают role/aria/dataset (клики вешает экран). */
  readonly interactive?: boolean;
  /** aria-label сетки (переопределяет labels.fieldAria, если задан явно — обратная совм.). */
  readonly ariaLabel?: string;
  /**
   * Явные локализованные подписи (Очки/Ходы/поле/клетка). Если не заданы — берутся из
   * `t()` по глобальному locale. Передаётся для НЕ-глобального языка (напр. SEO-фигуры
   * языка страницы), чтобы плашка была на нужном языке независимо от активного locale.
   */
  readonly labels?: Partial<BoardViewLabels>;
}

export interface BoardViewHandle {
  /** Корень доски (.board-frame) — экран кладёт его в свою область (.postament-board). */
  readonly frame: HTMLElement;
  /** Контейнер сетки (.board). */
  readonly boardEl: HTMLElement;
  /** 81 клетка (row-major) — для интерактива/анимаций экрана. */
  readonly cells: readonly HTMLDivElement[];
  /** Счёт на плашке. */
  setScore(n: number): void;
  /** Ходы на плашке. */
  setMoves(n: number): void;
  /** 3 следующих шара в нише (show=false — спрятать, напр. режим «вслепую»). */
  renderNiche(next: readonly NextBall[], show?: boolean): void;
  /** Статичная отрисовка поля (шары .ball.static); ghosts — predict-превью. */
  renderStaticGrid(grid: readonly CellValue[][], ghosts?: readonly NextBall[]): void;
  unmount(): void;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

/** HTML-escape для подписей плашки/aria, инлайнящихся в innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function mountBoardView(host: HTMLElement, opts: BoardViewOptions = {}): BoardViewHandle {
  const interactive = !!opts.interactive;

  // Локализованные подписи: явный opts.labels поверх дефолта из t() (глобальный locale).
  const labels: BoardViewLabels = { ...defaultLabels(), ...opts.labels };
  // ariaLabel (legacy-параметр) имеет приоритет над labels.fieldAria — обратная совм.
  const fieldAria = opts.ariaLabel ?? labels.fieldAria;

  const frame = document.createElement('div');
  // .board-frame--static в снимке → board.css снимает cursor:pointer/hover с клеток.
  frame.className = interactive ? 'board-frame' : 'board-frame board-frame--static';
  // Плашка (Вариант А): «Очки» слева; справа группа [«Ходы» + 3 шара] — Ходы рядом
  // с шарами, без наезда (frame-next = space-between из board.css). Подписи — i18n.
  frame.innerHTML = `
    <div class="frame-next">
      <div class="fn-score">
        <div class="fn-score-k">${esc(labels.score)}</div>
        <div class="fn-score-v" data-bv-score>0</div>
      </div>
      <div class="fn-right">
        <div class="fn-score fn-score--moves">
          <div class="fn-score-k">${esc(labels.moves)}</div>
          <div class="fn-score-v" data-bv-moves>0</div>
        </div>
        <div class="fn-niche" data-bv-niche aria-hidden="true"></div>
      </div>
    </div>
    <div class="board" role="${interactive ? 'grid' : 'img'}" aria-label="${esc(fieldAria)}"></div>
  `;
  host.appendChild(frame);

  const boardEl = frame.querySelector<HTMLElement>('.board')!;
  const scoreEl = frame.querySelector<HTMLElement>('[data-bv-score]')!;
  const movesEl = frame.querySelector<HTMLElement>('[data-bv-moves]')!;
  const nicheEl = frame.querySelector<HTMLElement>('[data-bv-niche]')!;

  // 81 клетка (один раз).
  const cells: HTMLDivElement[] = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const d = document.createElement('div');
      d.className = 'cell';
      if (interactive) {
        d.setAttribute('role', 'gridcell');
        // Локализованный шаблон «Клетка {r},{c}» → подставляем 1-based координаты.
        d.setAttribute(
          'aria-label',
          labels.cellAria.replace('{r}', String(r + 1)).replace('{c}', String(c + 1)),
        );
        d.dataset['r'] = String(r);
        d.dataset['c'] = String(c);
      }
      boardEl.appendChild(d);
      cells.push(d);
    }
  }

  return {
    frame,
    boardEl,
    cells,
    setScore(n: number): void {
      scoreEl.textContent = formatNumber(n);
    },
    setMoves(n: number): void {
      movesEl.textContent = String(n);
    },
    renderNiche(next: readonly NextBall[], show = true): void {
      nicheEl.style.visibility = show ? 'visible' : 'hidden';
      let html = '';
      for (let i = 0; i < NEXT_BALLS_PER_TURN; i++) {
        const g = next[i];
        html += `<div class="fn-slot">${g ? `<div class="ball static bc${g.color}"></div>` : ''}</div>`;
      }
      nicheEl.innerHTML = html;
    },
    renderStaticGrid(grid: readonly CellValue[][], ghosts?: readonly NextBall[]): void {
      const ghostIndex = new Array<number>(N * N).fill(0);
      if (ghosts) {
        for (const g of ghosts) ghostIndex[g.r * N + g.c] = g.color;
      }
      for (let r = 0; r < N; r++) {
        for (let c = 0; c < N; c++) {
          const i = r * N + c;
          const cell = cells[i];
          if (!cell) continue;
          const v = grid[r]?.[c] ?? 0;
          if (v !== 0) {
            cell.innerHTML = `<div class="ball static bc${v}"></div>`;
          } else if (ghostIndex[i]) {
            cell.innerHTML = `<div class="ball ghost static bc${ghostIndex[i]}"></div>`;
          } else {
            cell.innerHTML = '';
          }
        }
      }
    },
    unmount(): void {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    },
  };
}
